import { useEffect, useState } from 'react'
import Chip from '@mui/joy/Chip'
import dayjs from 'dayjs'

function getRemainingSeconds(expiredAt: string | null, nowTimestamp: number): number | null {
    if (!expiredAt) {
        return null
    }

    const differenceInSeconds = dayjs(expiredAt).diff(dayjs(nowTimestamp), 'second')

    if (Number.isNaN(differenceInSeconds)) {
        return null
    }

    return Math.max(differenceInSeconds, 0)
}

function getExpiryChipLabel(remainingSeconds: number | null): string | null {
    if (remainingSeconds === null) {
        return null
    }

    const minutes = Math.floor(remainingSeconds / 60)
    const seconds = remainingSeconds % 60

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export function isReservationExpiringSoon(remainingSeconds: number | null): boolean {
    return remainingSeconds !== null && remainingSeconds <= 30
}

export default function ReservationExpiryChip({
    expiredAt,
    size,
}: {
    expiredAt?: string | null,
    size: 'sm' | 'md',
}) {
    const [nowTimestamp, setNowTimestamp] = useState(() => Date.now())

    useEffect(() => {
        if (!expiredAt) {
            return
        }

        const expiryTimestamp = dayjs(expiredAt).valueOf()

        if (Number.isNaN(expiryTimestamp)) {
            return
        }

        if (expiryTimestamp <= Date.now()) {
            setNowTimestamp(expiryTimestamp)
            return
        }

        const intervalId = window.setInterval(() => {
            const nextTimestamp = Date.now()

            if (nextTimestamp >= expiryTimestamp) {
                setNowTimestamp(expiryTimestamp)
                window.clearInterval(intervalId)
                return
            }

            setNowTimestamp(nextTimestamp)
        }, 1_000)

        return () => {
            window.clearInterval(intervalId)
        }
    }, [expiredAt])

    const remainingSeconds = getRemainingSeconds(expiredAt ?? null, nowTimestamp)
    const expiryChipLabel = getExpiryChipLabel(remainingSeconds)
    const isExpiringSoon = isReservationExpiringSoon(remainingSeconds)

    if (!expiryChipLabel) {
        return null
    }

    return (
        <Chip
            color={isExpiringSoon ? 'danger' : 'success'}
            variant="solid"
            size={size}
            sx={{
                backgroundColor: isExpiringSoon ? 'danger.500' : 'customGreen.main',
            }}
        >
            {expiryChipLabel}
        </Chip>
    )
}
